/**
 * ResponderMap - Road Closure Tracking App
 * For first responders to track road closures during severe weather events
 *
 * @format
 */

import React, {useEffect, Component, ErrorInfo} from 'react';
import {StatusBar, useColorScheme, View, Text, StyleSheet, InteractionManager, Dimensions, Platform, Linking, Alert} from 'react-native';
// Use SafeAreaProvider with initialMetrics to avoid native module call on iOS
import {SafeAreaProvider, initialWindowMetrics} from 'react-native-safe-area-context';
import {GestureHandlerRootView} from 'react-native-gesture-handler';

// Provide fallback metrics to avoid native module crash on iOS with New Architecture
const fallbackMetrics = {
  frame: { x: 0, y: 0, width: Dimensions.get('window').width, height: Dimensions.get('window').height },
  insets: { top: Platform.OS === 'ios' ? 47 : 0, left: 0, right: 0, bottom: Platform.OS === 'ios' ? 34 : 0 },
};
import AsyncStorage from '@react-native-async-storage/async-storage';
import {AuthProvider} from './src/contexts/AuthContext';
// Use SimpleNavigator to bypass react-native-screens compatibility issues on iOS
import SimpleNavigator from './src/navigation/SimpleNavigator';
import {setupNotificationHandlers, requestNotificationPermissions, initializeNotificationChannel} from './src/services/notifications';
import {getFirebaseInitialized} from './src/config/firebase';
import {SETTINGS_KEYS} from './src/screens/SettingsScreen';

// Error Boundary to catch any rendering errors
class ErrorBoundary extends Component<{children: React.ReactNode}, {hasError: boolean; error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error: Error) {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#ff6b6b', padding: 20}}>
          <Text style={{color: 'white', fontSize: 20, fontWeight: 'bold', marginBottom: 10}}>Something went wrong</Text>
          <Text style={{color: 'white', fontSize: 14, textAlign: 'center'}}>{this.state.error?.message || 'Unknown error'}</Text>
        </View>
      );
    }
    return this.props.children;
  }
}

// Initialize default notification sounds on first install
const initializeDefaultSounds = async () => {
  try {
    const [storedSoundClosedUri, storedSoundClosedName, storedSoundOpenedUri, storedSoundOpenedName] = await Promise.all([
      AsyncStorage.getItem(SETTINGS_KEYS.SOUND_CLOSED_URI),
      AsyncStorage.getItem(SETTINGS_KEYS.SOUND_CLOSED_NAME),
      AsyncStorage.getItem(SETTINGS_KEYS.SOUND_OPENED_URI),
      AsyncStorage.getItem(SETTINGS_KEYS.SOUND_OPENED_NAME),
    ]);

    // Set default sounds to system default if not already set
    if (!storedSoundClosedUri && !storedSoundClosedName) {
      await Promise.all([
        AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_URI, 'system_default'),
        AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_NAME, 'system_default'),
      ]);
      console.log('🔊 Initialized default Road Closed sound: System Default');
    }

    if (!storedSoundOpenedUri && !storedSoundOpenedName) {
      await Promise.all([
        AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_URI, 'system_default'),
        AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_NAME, 'system_default'),
      ]);
      console.log('🔊 Initialized default Road Opened sound: System Default');
    }
  } catch (error) {
    console.error('Error initializing default sounds:', error);
  }
};

function App(): React.JSX.Element {
  console.log('🔴 APP FUNCTION CALLED - THIS SHOULD APPEAR IN CONSOLE');
  const isDarkMode = useColorScheme() === 'dark';

  // TEMPORARY TEST - Remove after debugging
  // Uncomment to test if basic rendering works:
  // return (
  //   <View style={{flex: 1, backgroundColor: 'red', justifyContent: 'center', alignItems: 'center'}}>
  //     <Text style={{color: 'white', fontSize: 30}}>TEST - APP IS RENDERING!</Text>
  //   </View>
  // );

  useEffect(() => {
    console.log('🟢 App useEffect running...');
    
    // Handle deep links for Stripe subscription callbacks (Android only)
    // On iOS, subscription flows go through the website so these deep links won't fire
    const handleDeepLink = (event: { url: string }) => {
      const url = event.url;
      console.log('🔗 Deep link received:', url);
      
      if (Platform.OS === 'ios') {
        return;
      }

      if (url.startsWith('respondermap://subscription-success')) {
        Alert.alert(
          'Subscription Successful!',
          'Thank you for subscribing to ResponderMap. Your subscription is now active.',
          [{text: 'OK'}]
        );
      } else if (url.startsWith('respondermap://subscription-cancel')) {
        Alert.alert(
          'Checkout Canceled',
          'You can complete your subscription anytime from the Account screen.',
          [{text: 'OK'}]
        );
      }
    };

    // Check if app was opened from a deep link
    Linking.getInitialURL().then((url) => {
      if (url) {
        console.log('🔗 App opened with deep link:', url);
        handleDeepLink({ url });
      }
    }).catch(err => console.log('Error getting initial URL:', err));

    // Listen for deep links while app is running
    const linkingSubscription = Linking.addEventListener('url', handleDeepLink);
    
    // Setup notification handlers IMMEDIATELY so cold-start notification
    // data reaches AsyncStorage before MapScreen finishes its first render.
    if (getFirebaseInitialized()) {
      console.log('🔔 Setting up notifications...');
      setupNotificationHandlers();
      console.log('🔔 Notification setup complete');
    }

    // Defer non-critical notification work to avoid blocking UI
    InteractionManager.runAfterInteractions(() => {
      initializeDefaultSounds().catch(err => 
        console.log('Sound init error (non-critical):', err)
      );
      
      if (getFirebaseInitialized()) {
        initializeNotificationChannel().catch(err =>
          console.log('Notification channel error:', err)
        );
        
        // On web, browsers (Safari especially) require Notification.requestPermission()
        // to be called from a user gesture. The Settings screen exposes a button that
        // triggers this on click. Auto-prompting here throws a silent error in Safari
        // and is being deprecated in Chrome too.
        if (Platform.OS !== 'web') {
          setTimeout(() => {
            requestNotificationPermissions().catch(err =>
              console.log('Notification permission error:', err)
            );
          }, 1000);
        }
      }
    });
    
    console.log('🟢 App useEffect complete');

    // Initialize IAP connection on iOS only (dynamic import to avoid loading native module on Android)
    let iapCleanup: (() => void) | null = null;
    if (Platform.OS === 'ios') {
      import('./src/services/iapService').then(({iapService}) => {
        iapService.connect().catch(err =>
          console.log('IAP connect error (non-critical):', err)
        );
        iapCleanup = () => {
          iapService.disconnect().catch(() => {});
        };
      }).catch(err => console.log('IAP import error (non-critical):', err));
    }

    return () => {
      linkingSubscription.remove();
      iapCleanup?.();
    };
  }, []);

  // Show configuration message if Firebase isn't set up
  if (!getFirebaseInitialized()) {
    return (
      <GestureHandlerRootView style={{flex: 1, backgroundColor: '#f5f5f5'}}>
        <SafeAreaProvider initialMetrics={initialWindowMetrics || fallbackMetrics} style={{flex: 1}}>
          <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
          <View style={styles.configContainer}>
            <Text style={styles.configTitle}>Firebase Configuration Required</Text>
            <Text style={styles.configText}>
              Please configure Firebase to use this app:{'\n\n'}
              1. Create a Firebase project{'\n'}
              2. Add google-services.json to android/app/{'\n'}
              3. Add GoogleService-Info.plist to ios/ResponderMapNew/{'\n'}
              4. Rebuild the app
              {'\n\n'}
              See SETUP.md for detailed instructions.
            </Text>
          </View>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    );
  }

  // FULL APP with SimpleNavigator (bypasses react-native-screens)
  console.log('🔴 FULL APP: Loading with SimpleNavigator...');
  return (
    <GestureHandlerRootView style={{flex: 1, backgroundColor: '#424242'}}>
      <SafeAreaProvider initialMetrics={initialWindowMetrics || fallbackMetrics} style={{flex: 1}}>
        <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
        <AuthProvider>
          <SimpleNavigator />
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  configContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  configTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
    textAlign: 'center',
  },
  configText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
  },
});

export default App;
